import $ from "jquery";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { autorun, observable, reaction, runInAction, when } from "mobx";

import { browserSupported, platform } from "./browser-support-hero.js";
import JumboPlayer from "./media-player.js";

// DECLARE INITIAL STATE
export let player;
export let heroState = observable({
  ...WP.hero,
  browserSupported,
  platform,
});

// MEDIA TYPE COLLECTIONS
// ? simplifies conditional display logic for UI elements of like media types
// ? exported so other files can rely on any collection
export const autoloadTypes = ["zoom", "jitsi", "rally"],
  browserRestrictedTypes = ["zoom", "jitsi", "rally", "ivs", "cloudflare"],
  clipTypes = ["vimeo", "youtube", "wavesurfer", "image", "pdf", "link"],
  liveTypes = [
    "liveVimeo",
    "liveYoutube",
    "zoom",
    "jitsi",
    "rally",
    "ivs",
    "cloudflare",
    "royalslider",
  ],
  videojsTypes = ["ivs", "cloudflare", "liveIvs", "liveCloudflare"],
  vimeoStates = ["plastic", "vimeo", "liveVimeo"],
  youtubeStates = ["plastic", "youtube", "liveYoutube"],
  ivsStates = ["plastic", "ivs", "liveIvs"],
  cloudflareStates = ["plastic", "cloudflare", "liveCloudflare"];

// DYNAMIC HERO & INFO BAR
(function () {
  // escape hatch
  if (!document.querySelector(".event-hero-content")) return;

  // Initialize
  const init = () => {
    player = new JumboPlayer({
      type: heroState.media.type,
      id: heroState.media.id,
      isBackground: isBackground(heroState),
    });
    player.init();

    //? autoloads don't get "unwrapped", so we set the caption directly
    if (autoloadTypes.includes(player.type)) {
      runInAction(() => (heroState.caption = heroState.liveTitle));
    }
  };
  if (
    heroState.mode === "live" ||
    (heroState.mode !== "live" && heroState.media.type === "vimeo") ||
    (heroState.mode !== "live" && heroState.media.type === "royalslider")
  ) {
    init();
  }

  // Unwrap
  if (heroState.currentType === "plastic") {
    when(
      () => heroState.currentType !== "plastic",
      () => {
        // re-initialize the player because it's no longer a background video
        player.destroy();
        player = new JumboPlayer({
          type: heroState.media.type,
          id: heroState.media.id,
          isBackground: isBackground(heroState),
        });
        player.init();
      }
    );
  }

  // React to media change
  reaction(
    () => heroState.media,
    (nextMedia, prevMedia) => {
      window.scrollTo(0, 0);

      // init if haven't been initialized yet
      // ? corner case of playing a clip on an upcoming/archived page with no vimeo bg
      if (!player) {
        init();
        return;
      }

      // eject if re-playing same media
      if (isEqual(nextMedia, prevMedia)) return;

      // eject if the player was already (re)built for this media
      //? its possible for this reaction to run at the same time as an 'unwrap',
      //? such as when loading a clip param url, or clicking a clip before unrapping
      //? the 'live media'. In such cases, the "unwrap" when() above recreates
      //? the player before this reaction runs, so a redundant loadVideo() here
      //? would pause the player and interrupt its pending play()
      if (player.type === nextMedia.type && player.id === nextMedia.id) return;

      if (
        prevMedia.type === nextMedia.type &&
        player.isBackground === isBackground(heroState) &&
        //? vimeo sdk's .loadVideo() method fails when moving from a normal video/stream to an "event"
        //? so this check will force a full destroy and re-initialize of the hero in that case
        typeof prevMedia.id === typeof nextMedia.id
      ) {
        // new media of same type
        player.updateMedia(nextMedia.id);
      } else {
        // new media of new type
        player.destroy();
        player = new JumboPlayer({
          type: nextMedia.type,
          id: nextMedia.id,
          isBackground: isBackground(heroState),
        });
        player.init();
      }
    }
  );

  // React to an ended vimeo "live event"
  // ? by default, vimeo is showing the on-demand video recording of the livestream
  // ? instead we want the lobby, so we are destroying and-reinitializing the player
  reaction(
    () => heroState.resetLiveStream,
    (next) => {
      if (next === true) {
        player.destroy();
        player = new JumboPlayer({
          type: heroState.media.type,
          id: heroState.media.id,
          isBackground: isBackground(heroState),
        });
        player.init();
        runInAction(() => {
          heroState.resetLiveStream = false;
        });
      }
    }
  );

  // Pause on external link clicks
  if (
    heroState.media.type === "vimeo" ||
    heroState.media.type === "youtube" ||
    heroState.media.type === "wavesurfer"
  ) {
    let playerStatus = player.getPlayerStatus();
    let externalLink = false;

    $(".event-main").on("click", ".pause-video", function (e) {
      playerStatus = player.getPlayerStatus();
      externalLink = true;
      player.pause();
    });

    // if the page visibility changes to "visible", resume the video
    function handleVisibilityChange() {
      if (!document[hidden] && externalLink && playerStatus === "playing") {
        player.play();
        externalLink = false;
        playerStatus = player.getPlayerStatus();
      }
    }

    // Set the name of the hidden property and the change event for visibility
    let hidden, visibilityChange;
    if (typeof document.msHidden !== "undefined") {
      hidden = "msHidden";
      visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }

    // Handle page visibility change
    document.addEventListener(visibilityChange, handleVisibilityChange, false);
  }

  /**
   * Helper to determine if vimeo video should play as "background"
   *
   * ? bg videos hide controls, autoplay muted, and loop
   */
  function isBackground(state) {
    return (
      (state.currentType === "plastic" && !state.plasticDisable) ||
      (state.plasticDisable && ["upcoming", "archived"].includes(state.mode))
    );
  }

  const bodyEl = document.querySelector("body"),
    infobar = document.querySelector(".hero-infobar"),
    liveBadge = infobar.querySelector(".live-badge"),
    captionEl = infobar.querySelector(".hero-infobar-text"),
    returnBtn = infobar.querySelector(".hero-infobar-nav"),
    shareBtn = infobar.querySelector(".hero-infobar-share"),
    pdfBtn = infobar.querySelector(".hero-infobar-pdf-button"),
    imageBtn = infobar.querySelector(".hero-infobar-image-button"),
    heroInfobarOpenInZoom = infobar.querySelector(".hero-infobar-zoom-button"),
    hero = document.querySelector(".event-hero-content"),
    heroPlastic = Array.from(hero.querySelectorAll(".hero-plastic")),
    heroOverlay = hero.querySelector(".hero-overlay"),
    heroContent = hero.querySelector(".hero-content"),
    heroWrap = hero.querySelector(".hero-wrap"),
    heroMedia = hero.querySelector(".hero-media"),
    heroVideo = hero.querySelector(".hero-media-video"),
    heroWarning = hero.querySelector(".hero-browser-warning"),
    heroWarningMobile = hero.querySelector(".hero-browser-warning-mobile"),
    heroWarningDesktop = hero.querySelector(".hero-browser-warning-desktop"),
    heroWarningZoom = hero.querySelector(".hero-browser-warning-zoom"),
    heroZoomInfo = hero.querySelector(".hero-zoom-info"),
    heroOpenInZoom = hero.querySelector(".open-in-zoom"),
    heroZoomInfoClose = hero.querySelector(".hero-zoom-info-close");

  autorun(() => {
    const {
      caption,
      currentType,
      mode,
      plasticDisable,
      plasticType,
      plasticAspect,
    } = heroState;

    // HERO: browser warning
    heroWarning.style.display =
      !browserSupported && browserRestrictedTypes.includes(currentType)
        ? "flex"
        : "none";
    heroWarningDesktop.style.display =
      currentType !== "zoom" && platform !== "android" && platform !== "ios"
        ? "block"
        : "none";
    heroWarningMobile.style.display =
      currentType !== "zoom" && (platform === "android" || platform === "ios")
        ? "block"
        : "none";
    heroWarningZoom.style.display = currentType === "zoom" ? "block" : "none";

    // HERO: plastic wrap
    heroPlastic.forEach(function (div) {
      div.style.display = currentType === "plastic" ? "flex" : "none";
    });

    if (currentType === "plastic") {
      if (
        plasticDisable &&
        (plasticType === "vimeo" || plasticType === "youtube")
      ) {
        // ! this is pretty heinous, but we have to wait for the iframe to set the height here
        // !(we should refactor if/when we finally clientside-render the plastic wrap)
        const checkForVideo = setInterval(() => {
          if (heroVideo.querySelector("iframe")) {
            hero.style.minHeight =
              heroVideo.querySelector("iframe").offsetHeight + "px";
            clearInterval(checkForVideo);
          }
        }, 50);
      } else if (plasticType === "image") {
        hero.style.minHeight = 0;
        heroWrap.style.minHeight = 0;
      } else {
        hero.style.minHeight = heroMedia.offsetHeight + "px";
        heroWrap.style.minHeight = heroMedia.offsetHeight + "px";
      }
    } else {
      hero.style.minHeight = 0;
      heroWrap.style.minHeight = 0;
    }

    // HERO: overlay & content
    heroOverlay.style.display =
      currentType === "plastic" && !plasticDisable ? "flex" : "none";
    heroContent.style.display =
      currentType === "plastic" && !plasticDisable ? "flex" : "none";

    // HERO: media container
    heroMedia.dataset.type = currentType;
    heroMedia.style.position =
      currentType === "plastic" ? "absolute" : "relative";
    heroMedia.style.display =
      !browserSupported && browserRestrictedTypes.includes(currentType)
        ? "none"
        : "block";

    if (currentType !== "plastic" || plasticDisable) {
      heroMedia.classList.remove("is-blurred");
    } else {
      heroMedia.classList.add("is-blurred");
    }
    if (currentType === "plastic" && plasticAspect === "fill") {
      heroMedia.classList.add("is-fill");
    } else {
      heroMedia.classList.remove("is-fill");
    }

    // HERO: split-screen allowed on mobile
    if (
      currentType !== "plastic" &&
      currentType !== "rally" &&
      currentType !== "zoom" &&
      currentType !== "jitsi" &&
      currentType !== "image" &&
      currentType !== "pdf" &&
      currentType !== "royalslider"
    ) {
      bodyEl.classList.add("split-screen");
    } else {
      bodyEl.classList.remove("split-screen");
    }

    // INFOBAR
    infobar.style.display = currentType === "plastic" ? "none" : "flex";

    // INFOBAR: badge
    liveBadge.style.display =
      mode === "live" && liveTypes.includes(currentType)
        ? "inline-flex"
        : "none";

    // INFOBAR: title/caption
    captionEl.textContent = caption;
    captionEl.style.display = caption !== "" ? "inline-block" : "none";

    // INFOBAR: share button
    shareBtn.style.display = WP.eventShareDisable ? "none" : "block";

    // INFOBAR: zoom button
    heroInfobarOpenInZoom.style.display =
      currentType === "zoom" && browserSupported ? "inline-flex" : "none";

    // Show zoom session hero
    heroInfobarOpenInZoom.addEventListener("click", (e) => {
      heroZoomInfo.style.display = "flex";
      heroMedia.style.display = "none";
    });

    // Open in zoom, show success message
    heroOpenInZoom.addEventListener("click", (e) => {
      heroOpenInZoom.style.display = "none";
      heroZoomInfoClose.style.display = "none";
      heroZoomInfo.querySelector(".open-in-zoom-success").style.display =
        "block";
    });

    // Close zoom session hero
    heroZoomInfoClose.addEventListener("click", (e) => {
      heroZoomInfo.style.display = "none";
      heroMedia.style.display = "block";
    });

    // INFOBAR: pdf button
    pdfBtn.style.display = currentType === "pdf" ? "inline-flex" : "none";

    // INFOBAR: image button
    imageBtn.style.display = currentType === "image" ? "inline-flex" : "none";

    // INFOBAR: return button
    returnBtn.style.display =
      mode === "live" && clipTypes.includes(currentType) ? "block" : "none";
  });

  /**
   * Video.js needs some funky javascript help for styling when the aspect goes
   * wider than 16:9
   *
   * * (IVS and Cloudflare)
   */
  let threshold = false;
  function checkAspect() {
    if (videojsTypes.includes(heroState.media.type)) {
      const video = document.querySelector("#player video"),
        wrapper = video.closest(".video-js");

      if (!video || !wrapper) return;

      const getAspect = () => {
        const { height, width } = video.getBoundingClientRect();
        return height / width;
      };

      const setIt = () => {
        wrapper.style.padding = "0";
        wrapper.style.height = video.getBoundingClientRect().height + "px";
        threshold = true;
      };

      const forgetIt = () => {
        wrapper.style.padding = "0px 0px 56.25%";
        wrapper.style.height = "0";
        threshold = false;
      };

      forgetIt();
      const aspect = getAspect();
      if (!threshold && aspect < 0.5625) {
        setIt();
        return;
      }
      if (threshold && aspect >= 0.5625) {
        forgetIt();
      }
    }
  }
  if (videojsTypes.includes(heroState.media.type)) {
    requestAnimationFrame(() => checkAspect());
    onresize = debounce(checkAspect, 100);
    reaction(
      () => heroState.currentType,
      (next, prev) => {
        if (prev === "plastic" && videojsTypes.includes(next)) checkAspect();
      }
    );
  }
})();

export default heroState;
